import { getTranslations } from "@/lib/translations";
import { z } from "zod";

import { signIn as signInService, setPassword as setPasswordService, checkIsAuthorizedStaff } from "@/core/services/auth.service";
import { clearMustSetPassword } from "@/types/auth";
import { createClient } from "@/lib/supabase";

export type SignInState = {
  error?: string;
  redirectTo?: string;
};

export type SetPasswordState = {
  error?: string;
  redirectTo?: string;
};

function mapSignInErrorMessage(
  t: ReturnType<typeof getTranslations>,
  error: { code?: string; message?: string },
): string {
  if (error.code === "invalid_credentials") {
    return t("invalidCredentials");
  }

  if (error.code === "email_not_confirmed") {
    return t("emailNotConfirmed");
  }

  const message = (error.message ?? "").toLowerCase();

  if (
    message.includes("invalid login credentials") ||
    message.includes("invalid credentials")
  ) {
    return t("invalidCredentials");
  }

  return t("loginFailed");
}

export async function signIn(
  _prevState: SignInState,
  formData: FormData,
): Promise<SignInState> {
  const t = getTranslations("login");

  const signInSchema = z.object({
    email: z.email(),
    password: z.string().min(1, t("passwordRequired")),
    redirectTo: z.string().optional(),
  });

  const parsed = signInSchema.safeParse({
    email: formData.get("email"),
    password: formData.get("password"),
    redirectTo: formData.get("redirectTo") || undefined,
  });

  if (!parsed.success) {
    const emailIssue = parsed.error.issues.find(
      (issue) => issue.path[0] === "email",
    );
    if (emailIssue) {
      return { error: t("invalidEmail") };
    }

    const passwordIssue = parsed.error.issues.find(
      (issue) => issue.path[0] === "password",
    );
    if (passwordIssue) {
      return { error: t("passwordRequired") };
    }

    return { error: t("invalidCredentials") };
  }

  const result = await signInService(parsed.data.email, parsed.data.password);

  if (!result.success) {
    if (result.error === "Account inactive.") {
      return { error: t("accountInactive") };
    }

    if (result.error === "Access Denied: Restricted area.") {
      return { error: t("unauthorized") };
    }

    return { error: mapSignInErrorMessage(t, { message: result.error }) };
  }

  const destination =
    parsed.data.redirectTo && parsed.data.redirectTo.startsWith("/")
      ? parsed.data.redirectTo
      : "/dashboard";

  return { redirectTo: destination };
}

export async function setPassword(
  _prevState: SetPasswordState,
  formData: FormData,
): Promise<SetPasswordState> {
  const t = getTranslations("setPassword");

  const setPasswordSchema = z.object({
    password: z.string().min(6, t("passwordTooShort")),
    confirmPassword: z.string().min(1, t("confirmPasswordRequired")),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t("passwordMismatch"),
    path: ["confirmPassword"],
  });

  const parsed = setPasswordSchema.safeParse({
    password: formData.get("password"),
    confirmPassword: formData.get("confirmPassword"),
  });

  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      error: issue?.message ?? t("setPasswordFailed"),
    };
  }

  const supabase = await createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user?.email) {
    await supabase.auth.signOut();
    return { error: t("unauthorized") };
  }

  const staffRecord = await checkIsAuthorizedStaff(user.email);

  if (!staffRecord) {
    await supabase.auth.signOut();
    return { error: t("unauthorized") };
  }

  const result = await setPasswordService(
    parsed.data.password,
    parsed.data.confirmPassword,
  );

  if (!result.success) {
    return { error: result.error ?? t("setPasswordFailed") };
  }

  clearMustSetPassword();

  return { redirectTo: "/dashboard" };
}
